import React from 'react';
import { GridColDef, GridColumnGroupingModel } from '@mui/x-data-grid-premium';
import { PositionComparisonItem } from '../../../../types/generatedTypes';
import { PositionStatusBadge } from './PositionStatusBadge';

/**
 * Formats a number with appropriate decimal places.
 */
const formatNumber = (value: number | null, decimals: number = 2): string => {
  if (value === null || value === undefined) return '';
  return value.toLocaleString(undefined, {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

/**
 * Formats a delta value with sign and color indicator.
 */
const formatDelta = (
  value: number | null,
  isPercentage: boolean = false
): React.ReactNode => {
  if (value === null || value === undefined) return '';

  const formatted = isPercentage
    ? `${value >= 0 ? '+' : ''}${formatNumber(value)}%`
    : `${value >= 0 ? '+' : ''}${formatNumber(value)}`;

  const color =
    Math.abs(value) < 0.01 ? 'inherit' : value > 0 ? '#f44336' : '#4caf50';

  return <span style={{ color }}>{formatted}</span>;
};

/**
 * Returns the column definitions for the positions monitoring DataGrid.
 * Columns are ordered to match the reference layout:
 * Asset Info | Quantity (Bank, Illio, Delta) | Market Value (Bank, Illio, Delta) | Price (Bank, Illio, Delta) | Currency | Status
 */
export const getColumns = (): GridColDef<PositionComparisonItem>[] => [
  // Asset identifiers (after tree grouping column which shows asset name)
  {
    field: 'isin',
    headerName: 'ISIN',
    width: 130,
  },
  {
    field: 'symbol',
    headerName: 'Symbol',
    width: 100,
  },
  // Quantity group
  {
    field: 'bank_quantity',
    headerName: 'Bank Qty',
    width: 100,
    renderCell: params => formatNumber(params.value, 4),
  },
  {
    field: 'illio_quantity',
    headerName: 'Illio Qty',
    width: 100,
    renderCell: params => formatNumber(params.value, 4),
  },
  {
    field: 'quantity_delta',
    headerName: 'Qty Δ',
    width: 90,
    renderCell: params => formatDelta(params.value),
  },
  // Market Value group
  {
    field: 'bank_market_value',
    headerName: 'Bank Value',
    width: 110,
    renderCell: params => formatNumber(params.value),
  },
  {
    field: 'illio_market_value',
    headerName: 'Illio Value',
    width: 110,
    renderCell: params => formatNumber(params.value),
  },
  {
    field: 'value_delta',
    headerName: 'Value Δ',
    width: 100,
    renderCell: params => formatDelta(params.value),
  },
  // Price group
  {
    field: 'bank_price',
    headerName: 'Bank',
    width: 90,
    renderCell: params => formatNumber(params.value),
  },
  {
    field: 'illio_price',
    headerName: 'Illio',
    width: 90,
    renderCell: params => formatNumber(params.value),
  },
  {
    field: 'price_delta',
    headerName: 'Price Δ',
    width: 90,
    renderCell: params => formatDelta(params.value),
  },
  // Currency
  {
    field: 'currency',
    headerName: 'Currency',
    width: 80,
  },
  // Status (last column - flex: 1 fills remaining space to eliminate empty column)
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    minWidth: 140,
    renderCell: params => {
      if (params.row.isGroup) return null;
      return <PositionStatusBadge status={params.value} />;
    },
  },
];

/**
 * Column grouping model for visual grouping of related columns.
 * Groups: Quantity, Market Value, Price
 */
export const columnGroupingModel: GridColumnGroupingModel = [
  {
    groupId: 'quantity',
    headerName: 'Quantity',
    headerAlign: 'center',
    children: [
      { field: 'bank_quantity' },
      { field: 'illio_quantity' },
      { field: 'quantity_delta' },
    ],
  },
  {
    groupId: 'market_value',
    headerName: 'Market Value',
    headerAlign: 'center',
    children: [
      { field: 'bank_market_value' },
      { field: 'illio_market_value' },
      { field: 'value_delta' },
    ],
  },
  {
    groupId: 'price',
    headerName: 'Price',
    headerAlign: 'center',
    children: [
      { field: 'bank_price' },
      { field: 'illio_price' },
      { field: 'price_delta' },
    ],
  },
];
